import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { axialToWorld, getHexTileMapKey } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import { fromMilli, type SimState, type Unit } from '@/game/sim';
import { healVerb } from '@/game/view/heals';
import { tickHealings } from '@/game/view/tickCache';
import { useFrameStore, useLiveReaders } from './app/simFrame';
import { DEFAULT_UNIT_MUZZLE, HEAL_COLOR, healVisualFor, REPAIR_COLOR } from './models';

type Kind = 'heal' | 'repair';

const TILE_RADIUS = config.hexMap.tile.radius;
const GROUND_Y = config.hexMap.tile.thickness;
/** Where the mending lands on a unit: about its chest. */
const PATIENT_Y = GROUND_Y + 0.25;

/** Heal links drawn at once; more than this aren't drawn. */
const MAX_LINKS = 48;
/** Motes drifting along each link's beam, and rising round its patient. */
const BEAM_MOTES = 4;
const RISE_MOTES = 3;
const BEAM_WIDTH = 0.035;
/** Seconds a link takes to fade in or out. */
const FADE_SECONDS = 0.25;
/** Beam motes travel healer → patient this many times a second. */
const MOTE_SPEED = 0.9;
/** How high (world units) rising motes climb over their life, and how many a second start. */
const RISE_HEIGHT = 0.35;
const RISE_SPEED = 0.7;

type MeshName = `${Kind}Beam` | `${Kind}Mote`;
const MESH_NAMES: readonly MeshName[] = ['healBeam', 'healMote', 'repairBeam', 'repairMote'];

/** One healer's link to the unit it mends, as drawn. */
interface Link {
  active: boolean;
  healer: string;
  patient: string;
  kind: Kind;
  /** Source height on the healer's model. */
  sourceY: number;
  /** 0–1: fades in while the heal goes on, out once it stops. */
  strength: number;
  /** Listed in the latest state's heals (and in sight). */
  live: boolean;
  from: THREE.Vector3;
  to: THREE.Vector3;
  /** Phase offset (0–1) so neighbouring links' motes don't move in step. */
  seed: number;
}

function glow(color: string, opacity: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
}

// Boxes point along +z (Object3D.lookAt) and are stretched per instance.
const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
const moteGeometry = new THREE.OctahedronGeometry(1, 0);
const PER_LINK_MOTES = BEAM_MOTES + RISE_MOTES;
const MESHES: Record<
  MeshName,
  { geometry: THREE.BufferGeometry; material: THREE.Material; max: number }
> = {
  healBeam: { geometry: boxGeometry, material: glow(HEAL_COLOR, 0.35), max: MAX_LINKS },
  healMote: {
    geometry: moteGeometry,
    material: glow(HEAL_COLOR, 0.9),
    max: MAX_LINKS * PER_LINK_MOTES,
  },
  repairBeam: { geometry: boxGeometry, material: glow(REPAIR_COLOR, 0.4), max: MAX_LINKS },
  repairMote: {
    geometry: moteGeometry,
    material: glow(REPAIR_COLOR, 1),
    max: MAX_LINKS * PER_LINK_MOTES,
  },
};

function noRaycast() {}

/** A stable fraction in [0, 1) from an id, to offset a link's motes. */
function phase(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return ((h >>> 0) % 1000) / 1000;
}

/** Where a unit is drawn (as UnitsLayer lerps it along its path), at height `y`. */
export function unitWorld(unit: Unit, y: number, out: THREE.Vector3): THREE.Vector3 {
  let q = unit.pos.q;
  let r = unit.pos.r;
  const next = unit.path?.[0];
  if (next) {
    const p = fromMilli(unit.progress ?? 0);
    q += (next.q - q) * p;
    r += (next.r - r) * p;
  }
  const world = axialToWorld({ q, r }, TILE_RADIUS);
  return out.set(world.x, y, world.z);
}

/**
 * Healing in progress (`healings`: who the sim's heal step mends each tick):
 * a soft beam from each healer's staff, medkit or wrench to the unit it
 * mends, with motes drifting along it and rising round the patient — green
 * for a heal, amber sparks for an engineer's repair. Links fade in and out
 * as heals start and stop, and follow both units as they move.
 *
 * Fog-safe: a link is drawn only while both the healer's and the patient's
 * tiles are in the viewer's sight. Fixed pools drawn as instanced meshes
 * (nothing allocated per frame), not raycastable.
 */
export function HealsLayer() {
  const { getState, getTileVisibility } = useLiveReaders();
  const rules = useFrameStore().get().rules;
  const links = useMemo(() => {
    const pool: Link[] = [];
    for (let i = 0; i < MAX_LINKS; i++) {
      pool.push({
        active: false,
        healer: '',
        patient: '',
        kind: 'heal',
        sourceY: 0,
        strength: 0,
        live: false,
        from: new THREE.Vector3(),
        to: new THREE.Vector3(),
        seed: 0,
      });
    }
    return pool;
  }, []);
  const byHealer = useMemo(() => new Map<string, Link>(), []);
  const lastTickRef = useRef(-1);
  const meshRefs = useRef<Partial<Record<MeshName, THREE.InstancedMesh>>>({});
  const scratch = useMemo(
    () => ({
      obj: new THREE.Object3D(),
      goal: new THREE.Vector3(),
      counts: { healBeam: 0, healMote: 0, repairBeam: 0, repairMote: 0 } as Record<
        MeshName,
        number
      >,
    }),
    []
  );

  function put(name: MeshName, obj: THREE.Object3D): void {
    const mesh = meshRefs.current[name];
    const counts = scratch.counts;
    if (!mesh || counts[name] >= MESHES[name].max) return;
    obj.updateMatrix();
    mesh.setMatrixAt(counts[name]++, obj.matrix);
  }

  /** Marks every link live or not from a new state, starting links for new heals. */
  function sync(state: SimState): void {
    const seen = (u: Unit) => getTileVisibility(getHexTileMapKey(u.pos)) === 'visible';
    for (const link of links) link.live = false;
    for (const [healerId, patientId] of tickHealings(state, rules)) {
      const healer = state.units[healerId];
      const patient = state.units[patientId];
      if (!healer || !patient || !seen(healer) || !seen(patient)) continue;
      let link = byHealer.get(healerId);
      // Switched patients: the beam swings over rather than restarting.
      if (link) link.patient = patientId;
      else {
        link = links.find((l) => !l.active);
        if (!link) continue;
        const visual = healVisualFor(healer.type);
        link.active = true;
        link.healer = healerId;
        link.patient = patientId;
        link.kind = visual?.kind ?? healVerb(rules, healer.type);
        link.sourceY = GROUND_Y + (visual?.source ?? DEFAULT_UNIT_MUZZLE).y;
        link.strength = 0;
        link.seed = phase(healerId);
        unitWorld(healer, link.sourceY, link.from);
        unitWorld(patient, PATIENT_Y, link.to);
        byHealer.set(healerId, link);
      }
      link.live = true;
    }
    // A heal that stopped in sight fades out; one gone into the fog goes at once.
    for (const link of links) {
      if (!link.active || link.live) continue;
      const healer = state.units[link.healer];
      const patient = state.units[link.patient];
      if (!healer || !patient || !seen(healer) || !seen(patient)) link.strength = 0;
    }
  }

  useFrame(({ clock }, delta) => {
    const state = getState();
    const now = clock.elapsedTime;
    if (state.tick !== lastTickRef.current) {
      lastTickRef.current = state.tick;
      sync(state);
    }

    const { obj, goal, counts } = scratch;
    for (const name of MESH_NAMES) counts[name] = 0;
    const follow = 1 - Math.exp(-12 * delta);
    const fade = delta / FADE_SECONDS;

    for (const link of links) {
      if (!link.active) continue;
      const healer = state.units[link.healer];
      const patient = state.units[link.patient];
      link.strength = Math.min(1, Math.max(0, link.strength + (link.live ? fade : -fade)));
      if (link.strength <= 0 || !healer || !patient) {
        link.active = false;
        byHealer.delete(link.healer);
        continue;
      }
      link.from.lerp(unitWorld(healer, link.sourceY, goal), follow);
      link.to.lerp(unitWorld(patient, PATIENT_Y, goal), follow);
      const s = link.strength;

      // The beam, gently pulsing.
      obj.rotation.set(0, 0, 0);
      obj.position.lerpVectors(link.from, link.to, 0.5);
      if (link.from.distanceToSquared(link.to) > 1e-8) obj.lookAt(link.to);
      const width = BEAM_WIDTH * s * (0.8 + 0.2 * Math.sin(now * 6 + link.seed * 7));
      obj.scale.set(width, width, link.from.distanceTo(link.to));
      put(`${link.kind}Beam`, obj);

      // Motes drifting along it, bobbing a little above the line.
      obj.rotation.set(0, 0, 0);
      for (let i = 0; i < BEAM_MOTES; i++) {
        const t = (now * MOTE_SPEED + i / BEAM_MOTES + link.seed) % 1;
        obj.position.lerpVectors(link.from, link.to, t);
        obj.position.y += 0.04 * Math.sin((t + link.seed) * Math.PI * 4);
        obj.scale.setScalar(0.028 * s * Math.sin(Math.PI * t));
        obj.rotation.y = t * Math.PI * 2;
        put(`${link.kind}Mote`, obj);
      }
      // Rising round the patient (a repair's sparks stay lower and quicker).
      const rise = link.kind === 'repair' ? 0.5 : 1;
      for (let i = 0; i < RISE_MOTES; i++) {
        const t = (now * RISE_SPEED * (2 - rise) + i / RISE_MOTES + link.seed) % 1;
        const angle = (i / RISE_MOTES + link.seed) * Math.PI * 2 + now * 0.8;
        obj.position.set(
          link.to.x + Math.cos(angle) * 0.14,
          link.to.y - 0.12 + t * RISE_HEIGHT * rise,
          link.to.z + Math.sin(angle) * 0.14
        );
        obj.scale.setScalar(0.024 * s * (1 - t));
        put(`${link.kind}Mote`, obj);
      }
    }

    for (const name of MESH_NAMES) {
      const mesh = meshRefs.current[name];
      if (!mesh) continue;
      mesh.count = counts[name];
      mesh.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <>
      {MESH_NAMES.map((name) => {
        const { geometry, material, max } = MESHES[name];
        return (
          <instancedMesh
            key={name}
            ref={(mesh) => {
              if (mesh) {
                mesh.count = 0;
                mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
                meshRefs.current[name] = mesh;
              } else {
                delete meshRefs.current[name];
              }
            }}
            args={[geometry, material, max]}
            frustumCulled={false}
            raycast={noRaycast}
          />
        );
      })}
    </>
  );
}
